import React, { useState, useEffect } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { fmtDate } from "../utils/date";
import { tradeLogCharges, legCharges, assetClassForLeg } from "../utils/charges";
import { formatOptionSymbol, isOptionSymbol } from "../utils/optionSymbol";

// Per-leg date (NRML carry legs span many days) — "2026-06-12" → "12-Jun-2026".
const fmtDay = (d) => (d ? fmtDate(d) : "");
// Chronological sort key from a date (YYYY-MM-DD, optional) + time (HH:MM).
// Both are zero-padded, so plain string compare is correct. `fb` is the trade's
// close date — used for intraday (MIS) legs that carry no per-leg date.
const tkey = (date, time, fb) => (date || fb || "") + " " + (time || "99:99");
// Exit reason → colour + readable label, so the event type is scannable and the
// sequence (Roll → SL Hit → Target → Expiry) is obvious at a glance.
const EXIT_STYLE = {
  "SL HIT":          { bg: "#fee2e2", color: "#b91c1c", label: "SL Hit" },
  "TARGET":          { bg: "#dcfce7", color: "#15803d", label: "Target" },
  "ROLL":            { bg: "#ffedd5", color: "#c2410c", label: "Roll" },
  "EXPIRY":          { bg: "#e0e7ff", color: "#4338ca", label: "Expiry" },
  "SQUAREOFF":       { bg: "#f1f5f9", color: "#475569", label: "Square Off" },
  "OVERALL SL":      { bg: "#fee2e2", color: "#b91c1c", label: "Overall SL" },
  "OVERALL TARGET":  { bg: "#dcfce7", color: "#15803d", label: "Overall Target" },
  "INDICATOR EXIT":  { bg: "#f3e8ff", color: "#7e22ce", label: "Indicator Exit" },
  // Inside-Bar spot-based exits (baby-range SL / breakout target / trailing stop).
  "INSIDE BAR TRAIL":  { bg: "#cffafe", color: "#0e7490", label: "Trail Exit" },
  "INSIDE BAR TARGET": { bg: "#dcfce7", color: "#15803d", label: "IB Target" },
  "INSIDE BAR SL":     { bg: "#fee2e2", color: "#b91c1c", label: "IB SL" },
  "SL COST":           { bg: "#fef3c7", color: "#92400e", label: "SL → Cost" },
  // Auto SL/Target at the next pivot level (spot-based).
  "SR TARGET":         { bg: "#dcfce7", color: "#15803d", label: "Level Target" },
  "SR SL":             { bg: "#fee2e2", color: "#b91c1c", label: "Level SL" },
};
const exitStyle = (t) => EXIT_STYLE[t] || { bg: "#e0f2fe", color: "#0369a1", label: t || "—" };

// ── Per-leg live delta trace (Delta-Neutral Adjustment) ──────────────────────
// Shows how each short leg's Black-Scholes |Δ| evolves tick-by-tick through the
// cycle, with ADJ / MAXLOSS / TREND markers — so you can SEE the delta change as
// the simulation runs. Only present when the strategy ran Delta-Neutral Adjust.
const tdS = { padding: "4px 8px", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" };
function DeltaTrace({ series }) {
  const [open, setOpen] = useState(false);
  const adjN = series.filter(s => (s.event || "").startsWith("ADJ")).length;
  const evColor = (e) => e.startsWith("ADJ") ? { bg: "#ecfeff", color: "#0e7490" }
    : e === "MAXLOSS" ? { bg: "#fee2e2", color: "#b91c1c" }
    : e === "TREND" ? { bg: "#fef3c7", color: "#92400e" } : { bg: "transparent", color: "#64748b" };
  return (
    <div style={{ background: "#fbfdff", borderTop: "1px dashed #dbeafe" }}>
      <button onClick={() => setOpen(o => !o)} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 20px", background: "transparent", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#0e7490", textAlign: "left" }}>
        <span style={{ fontSize: 11 }}>{open ? "▾" : "▸"}</span>
        Δ trace — per-leg delta over the cycle ({series.length} ticks · {adjN} adjusts)
      </button>
      {open && (
        <div className="ar-tablewrap" style={{ maxHeight: 280, overflowY: "auto", overflowX: "auto", margin: "0 20px 12px", border: "1px solid #e5edf5", borderRadius: 8 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
            <thead>
              <tr>
                {["Time", "Spot", "CE", "CEΔ", "PE", "PEΔ", "|imb|", "Loss%", "Event"].map(h => (
                  <th key={h} style={{ position: "sticky", top: 0, background: "#f1f5f9", padding: "5px 8px", textAlign: "left", color: "#475569", fontWeight: 700, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {series.map((s, i) => {
                const ev = s.event || "";
                const es = evColor(ev);
                return (
                  <tr key={i} style={{ background: ev ? es.bg : (i % 2 ? "#fff" : "#fafcff") }}>
                    <td style={tdS}>{(s.d || "").slice(5)} {s.t}</td>
                    <td style={tdS}>{s.spot}</td>
                    <td style={tdS}>{s.ceK}</td>
                    <td style={{ ...tdS, fontWeight: 700, color: s.ceD >= 0.30 ? "#b91c1c" : "#0f172a" }}>{(s.ceD ?? 0).toFixed(3)}</td>
                    <td style={tdS}>{s.peK}</td>
                    <td style={{ ...tdS, fontWeight: 700, color: s.peD >= 0.30 ? "#b91c1c" : "#0f172a" }}>{(s.peD ?? 0).toFixed(3)}</td>
                    <td style={tdS}>{(s.imb ?? 0).toFixed(3)}</td>
                    <td style={{ ...tdS, color: s.lossPct >= 0 ? "#ef4444" : "#10b981" }}>{s.lossPct}</td>
                    <td style={{ ...tdS, fontWeight: ev ? 800 : 400, color: es.color }}>{ev || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Transaction details (grouped by date, expandable, CSV/PDF export) ──
// Renders ONLY the real legs the backend returns — never fabricates a row.
// Crypto (Delta) P&L is in USD; NSE in ₹. The backend tags each trade/result with
// assetClass, so the right symbol is shown without any extra plumbing.
const curSymbol = (ac) => String(ac || '').startsWith('crypto') ? '$' : '₹';

function TradeHistory({ tradeLog, focusReq, strategies, feeMode }) {
  const cur = curSymbol((tradeLog || []).find(t => t.assetClass)?.assetClass);
  // The strategy a trade came from (single-select = that one; multi-select = match by
  // underlying), used to deep-link the per-leg "View chart" button with the right
  // strategy overlay + timeframe.
  const stratFor = (t) => {
    const list = strategies || [];
    if (list.length === 1) return list[0];
    return list.find(s => (s.instrument || s.underlying) === t.instrument) || null;
  };
  const chartHref = (t, leg) => {
    const und = t.instrument || leg.symbol || "";
    const date = leg.entryDate || t.fullDate || "";
    const s = stratFor(t);
    const q = new URLSearchParams();
    // Open the chart on the SAME timeframe the strategy runs. Use the inside-bar TF
    // ONLY when inside-bar is actually enabled (its `timeframe` defaults to "5min"
    // in the schema even when off, which would otherwise override the real TF) →
    // then the indicator TF → the strategy's own candle timeframe (e.g. 60-min).
    const stratTf = (s?.insideBar?.enabled && s?.insideBar?.timeframe)
      || s?.indicatorConfig?.timeframe || s?.timeframe || s?.candleInterval;
    // Crypto OPTION leg (e.g. BTC24D03100000CE) → open the Strike sub-tab on THIS
    // contract's premium chart, parsed from the leg symbol — not the perp/spot. Perp
    // legs (BTCUSD, no CE/PE) fall through to the perp overlay chart below.
    const isCryptoOpt = cryptoTz && /^(BTC|ETH)\d{2}[1-9OND]\d{2}\d+(?:CE|PE)$/.test(String(leg.symbol || "").toUpperCase());
    if (isCryptoOpt) {
      q.set("view", "strike");
      q.set("symbol", leg.symbol);
      if (stratTf) q.set("tf", stratTf);
      return "/option-chart?" + q.toString();
    }
    // The clicked leg's OWN option symbol (e.g. NIFTY2661623200CE) so the chart opens on
    // THIS strike/expiry/CE-PE — not the index. Only real NSE option symbols qualify;
    // equity/crypto-perp legs fall back to the underlying/index path.
    if (isOptionSymbol(leg.symbol)) q.set("symbol", leg.symbol);
    if (und) q.set("underlying", und);
    if (date) q.set("date", date);
    if (stratTf) q.set("tf", stratTf);
    if (s?._id) q.set("strategyId", s._id);
    return "/option-chart?" + q.toString();
  };
  // Crypto trade times are emitted by the backtest engine in UTC (Delta is UTC-native;
  // _setTimezone sets offsetMin 0 for crypto) — matching the live engine, which gates
  // entry/exit on UTC too. So the primary tag is UTC; we also show the IST equivalent
  // (+5:30) since the builder's entry-time field is entered in IST. Roll the date forward
  // if IST crosses midnight. NSE times are already IST (offsetMin 330) → left untagged.
  const cryptoTz = (tradeLog || []).some(t => /^crypto/.test(t.assetClass || ''));
  const istOf = (dateStr, hhmm) => {
    if (!dateStr || !hhmm || !/^\d{1,2}:\d{2}$/.test(hhmm)) return null;
    const base = Date.parse(dateStr + 'T' + hhmm.padStart(5, '0') + ':00Z'); // UTC wall-clock as ms
    if (isNaN(base)) return null;
    const i = new Date(base + 330 * 60000); // +5:30 → IST
    const hm = String(i.getUTCHours()).padStart(2, '0') + ':' + String(i.getUTCMinutes()).padStart(2, '0');
    const day = i.toISOString().slice(0, 10);
    return day === dateStr ? hm : (fmtDay(day) + ' ' + hm); // include date only when it differs
  };
  const showDT = (dateStr, hhmm) => {
    const base = fmtDay(dateStr) + ' · ' + hhmm + (cryptoTz ? ' UTC' : '');
    if (!cryptoTz) return base;
    const i = istOf(dateStr, hhmm);
    return i ? base + ' / ' + i + ' IST' : base;
  };
  const [expandedDates, setExpandedDates] = useState({});
  // Transaction filters — applied at the LEG level (and reflected in the day P&L
  // header + CSV export, so "what you see is what you get"). ALL = no filter.
  const [fExit, setFExit] = useState("ALL");     // exit type (SL HIT / TARGET / …)
  const [fTxn, setFTxn] = useState("ALL");       // BUY / SELL
  const [fResult, setFResult] = useState("ALL"); // PROFIT / LOSS
  const [fQuery, setFQuery] = useState("");      // symbol search

  // Exit types actually present in this result, so the dropdown never offers an
  // empty option. Sorted for a stable order.
  const availableExitTypes = [...new Set(
    tradeLog.flatMap(t => (t.legs || []).map(l => l.exitType).filter(Boolean))
  )].sort();

  const q = fQuery.trim().toLowerCase();
  const anyFilter = fExit !== "ALL" || fTxn !== "ALL" || fResult !== "ALL" || q !== "";
  const legMatch = (leg) => {
    if (fExit !== "ALL" && (leg.exitType || "") !== fExit) return false;
    if (fTxn !== "ALL" && leg.action !== fTxn) return false;
    if (fResult === "PROFIT" && !(leg.pnl >= 0)) return false;
    if (fResult === "LOSS" && !(leg.pnl < 0)) return false;
    if (q && !formatOptionSymbol(leg.symbol).toLowerCase().includes(q)) return false;
    return true;
  };
  const clearFilters = () => { setFExit("ALL"); setFTxn("ALL"); setFResult("ALL"); setFQuery(""); };

  // A calendar-day click (focusReq carries its date + a bump counter) expands
  // that day's group and smooth-scrolls it into view.
  useEffect(() => {
    if (!focusReq || !focusReq.date) return;
    setExpandedDates(prev => ({ ...prev, [focusReq.date]: true }));
    const el = document.getElementById("txn-" + focusReq.date);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusReq]);

  const toggleDate = (date) => setExpandedDates(prev => ({ ...prev, [date]: !prev[date] }));

  const downloadCSV = () => {
    const rows = ["Trade Date,Symbol,Leg Type,Quantity,Entry Date,Entry Time,Entry Price,Entry Spot,Entry Delta,Exit Date,Exit Time,Exit Price,Exit Spot,Exit Delta,P&L,Charges,Transaction,Exit Type"];
    tradeLog.forEach(t => {
      // Same order as the on-screen table: last-closed first (exit time desc).
      // Only the legs that pass the active filters are exported.
      const ordered = [...(t.legs || [])].filter(legMatch).sort((p, q) => {
        const kp = tkey(p.exitDate, p.exitTime, t.fullDate), kq = tkey(q.exitDate, q.exitTime, t.fullDate);
        return kp < kq ? 1 : kp > kq ? -1 : 0;
      });
      ordered.forEach(l => {
        const legType = l.reentry > 0 ? `Re-entry #${l.reentry}` : "Entry";
        // "Trade Date" = the leg's OWN entry day (a carry/delta-adjust leg often fires
        // weeks before the cycle's close day), not the cycle close date t.fullDate.
        const tradeDate = l.entryDate || t.fullDate;
        const lCh = legCharges(l.entryPrice, l.exitPrice, l.qty, l.action, assetClassForLeg(l, t.assetClass), l.spotEntry, l.spotExit, feeMode);
        const lChStr = cur === "$" ? lCh.toFixed(2) : Math.round(lCh);
        rows.push(`${tradeDate},${formatOptionSymbol(l.symbol)},${legType},${l.qty},${l.entryDate || t.fullDate},${l.entryTime},${l.entryPrice},${l.spotEntry ?? ""},${l.entryDelta ?? ""},${l.exitDate || t.fullDate},${l.exitTime},${l.exitPrice},${l.spotExit ?? ""},${l.exitDelta ?? ""},${l.pnl},${lChStr},${l.action},${l.exitType}`);
      });
    });
    const a = document.createElement("a");
    a.href = "data:text/csv," + encodeURIComponent(rows.join("\n"));
    a.download = "backtest_transactions.csv"; a.click();
  };

  const downloadPDF = () => { window.print(); };

  const grouped = {};
  tradeLog.forEach(t => {
    const d = t.fullDate; // UTC day-key for crypto (engine is UTC), IST for NSE
    if (!grouped[d]) grouped[d] = [];
    grouped[d].push(t);
  });

  const C2 = {
    card: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "20px 24px", marginBottom: 16 },
    th: { padding: "10px 16px", textAlign: "left", fontSize: 12, color: "#9ca3af", fontWeight: 700, borderBottom: "1px solid #f1f3f9", background: "#fafafa" },
    td: { padding: "12px 16px", fontSize: 13, color: "#374151", borderBottom: "1px solid #f9fafb" },
  };

  return (
    <div style={C2.card}>
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Transaction Details</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <button onClick={downloadCSV} style={{ padding: "7px 14px", fontSize: 12, fontWeight: 600, borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", color: "#374151", display: "flex", alignItems: "center", gap: 5 }}>
            Download CSV ↓
          </button>
          <button onClick={downloadPDF} style={{ padding: "7px 14px", fontSize: 12, fontWeight: 600, borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", color: "#374151", display: "flex", alignItems: "center", gap: 5 }}>
            Download PDF ↓
          </button>
        </div>
      </div>

      {/* Filter bar — narrows the legs shown (and the day P&L + CSV export). */}
      {(() => {
        const sel = { padding: "7px 10px", fontSize: 12, fontWeight: 600, borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#374151", cursor: "pointer" };
        return (
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#64748b" }}>Filter:</span>
            <select value={fExit} onChange={e => setFExit(e.target.value)} style={sel} title="Exit type">
              <option value="ALL">All exit types</option>
              {availableExitTypes.map(t => <option key={t} value={t}>{exitStyle(t).label}</option>)}
            </select>
            <select value={fTxn} onChange={e => setFTxn(e.target.value)} style={sel} title="Transaction">
              <option value="ALL">Buy & Sell</option>
              <option value="SELL">Sell only</option>
              <option value="BUY">Buy only</option>
            </select>
            <select value={fResult} onChange={e => setFResult(e.target.value)} style={sel} title="Result">
              <option value="ALL">Profit & Loss</option>
              <option value="PROFIT">Profit legs</option>
              <option value="LOSS">Loss legs</option>
            </select>
            <input value={fQuery} onChange={e => setFQuery(e.target.value)} placeholder="Search symbol…" style={{ ...sel, cursor: "text", minWidth: 150 }} />
            {anyFilter && (
              <button onClick={clearFilters} style={{ ...sel, color: "#dc2626", borderColor: "#fecaca", background: "#fff5f5" }}>✕ Clear</button>
            )}
          </div>
        );
      })()}

      {Object.entries(grouped).sort(([a], [b]) => new Date(b) - new Date(a)).map(([date, trades]) => {
        // When a filter is active, drop trades whose legs all fail it, and skip the
        // whole day if nothing matches. The day P&L header then reflects only the
        // matching legs (so a "Loss legs" filter shows the loss subtotal, not the net).
        const matchTrades = anyFilter
          ? trades.filter(t => (t.legs || []).some(legMatch))
          : trades;
        if (anyFilter && !matchTrades.length) return null;
        const dayPnl = anyFilter
          ? matchTrades.reduce((s, t) => s + (t.legs || []).filter(legMatch).reduce((a, l) => a + (l.pnl || 0), 0), 0)
          : matchTrades.reduce((s, t) => s + t.pnl, 0);
        // Day charges = sum of each shown leg's round-trip charges (same rate table +
        // fee-mode as the per-leg column), so Net = Gross P&L − Charges reconciles
        // exactly with the leg rows below.
        const dayCharges = matchTrades.reduce((s, t) => s
          + ((anyFilter ? (t.legs || []).filter(legMatch) : (t.legs || []))
            .reduce((a, l) => a + legCharges(l.entryPrice, l.exitPrice, l.qty, l.action, assetClassForLeg(l, t.assetClass), l.spotEntry, l.spotExit, feeMode), 0)), 0);
        const dayNet = dayPnl - dayCharges;
        const money = (v) => cur === "$" ? v.toFixed(2) : Math.round(v).toLocaleString();
        const isExpanded = expandedDates[date] !== false;
        return (
          <div key={date} id={"txn-" + date} style={{ border: focusReq && focusReq.date === date ? "2px solid #2563eb" : "1px solid #e2e8f0", borderRadius: 10, marginBottom: 10, overflow: "hidden", scrollMarginTop: 80, transition: "border-color .2s" }}>
            <div onClick={() => toggleDate(date)} style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "space-between", alignItems: "center", padding: "12px 20px", background: "#fafafa", cursor: "pointer", userSelect: "none" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                {fmtDate(date)}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span title="Gross P&L before charges − estimated charges (brokerage + taxes)" style={{ fontSize: 11.5, color: "#9ca3af", fontWeight: 600 }}>
                  Gross {dayPnl >= 0 ? "+" : ""}{cur}{money(dayPnl)} − Charges {cur}{money(dayCharges)} =
                </span>
                <span style={{ fontSize: 12, color: "#6b7280" }}>Net P&L</span>
                <span style={{ fontSize: 13, fontWeight: 700, padding: "3px 10px", borderRadius: 6, background: dayNet >= 0 ? "#dcfce7" : "#fee2e2", color: dayNet >= 0 ? "#16a34a" : "#dc2626" }}>
                  {dayNet >= 0 ? "+" : ""}{cur}{money(dayNet)}
                </span>
                <span style={{ color: "#9ca3af", fontSize: 12 }}>{isExpanded ? "∧" : "∨"}</span>
              </div>
            </div>

            {isExpanded && (
              <div>
                <div className="ar-tablewrap" style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={C2.th}>Symbol</th>
                      <th style={C2.th}>Quantity</th>
                      <th style={C2.th}>Entry</th>
                      <th style={C2.th}>Exit</th>
                      <th style={C2.th}>Delta</th>
                      <th style={C2.th}>Profit/Loss</th>
                      <th style={C2.th}>Charges</th>
                      <th style={C2.th}>Transaction</th>
                      <th style={C2.th}>Exit Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...matchTrades].sort((a, b) => {
                      // Earliest cycle first within the day, so multi-cycle (NRML)
                      // days read in time order top-to-bottom.
                      const ta = tkey(a.legs?.[0]?.entryDate, a.legs?.[0]?.entryTime, a.fullDate);
                      const tb = tkey(b.legs?.[0]?.entryDate, b.legs?.[0]?.entryTime, b.fullDate);
                      return ta < tb ? -1 : ta > tb ? 1 : 0;
                    }).map((t, i) => {
                      // Apply the active filter to this cycle's legs; a filter that
                      // matches nothing here drops the cycle entirely.
                      const legs = anyFilter ? (t.legs || []).filter(legMatch) : (t.legs || []);
                      if (!legs.length) {
                        if (anyFilter) return null;
                        return (
                          <tr key={`${i}-none`}><td style={{ ...C2.td, color: "#9ca3af", fontStyle: "italic" }} colSpan={9}>No leg detail available for this trade</td></tr>
                        );
                      }
                      // Order legs by EXIT time DESCENDING — the leg that closed
                      // last sits on top. Tie-break on entry time (descending).
                      const ordered = [...legs].sort((p, q) => {
                        const kp = tkey(p.exitDate, p.exitTime, t.fullDate), kq = tkey(q.exitDate, q.exitTime, t.fullDate);
                        if (kp !== kq) return kp < kq ? 1 : -1;
                        const ep = tkey(p.entryDate, p.entryTime, t.fullDate), eq = tkey(q.entryDate, q.entryTime, t.fullDate);
                        return ep < eq ? 1 : ep > eq ? -1 : 0;
                      });
                      const legRows = ordered.map((leg, j) => {
                        const ex = exitStyle(leg.exitType);
                        // Chronological rank (1 = closed first), so the badge still
                        // shows the real sequence even though the list is newest-first.
                        const stepNo = ordered.length - j;
                        // Estimated round-trip charges for THIS leg (entry + exit orders),
                        // per the same rate table the summary uses — so per-leg + total reconcile.
                        const legCh = legCharges(leg.entryPrice, leg.exitPrice, leg.qty, leg.action, assetClassForLeg(leg, t.assetClass), leg.spotEntry, leg.spotExit, feeMode);
                        return (
                        <tr key={`${i}-${j}`} style={{ borderBottom: "1px solid #f9fafb", background: leg.reentry > 0 ? "#fffbeb" : "transparent" }}>
                          <td style={{ ...C2.td, fontWeight: 600, color: "#0f172a" }}>
                            <span title="Order in which this leg closed (1 = closed first)" style={{ display: "inline-flex", width: 18, height: 18, marginRight: 7, borderRadius: 5, background: "#eef2ff", color: "#4338ca", fontSize: 10, fontWeight: 800, alignItems: "center", justifyContent: "center", verticalAlign: "middle" }}>{stepNo}</span>
                            {formatOptionSymbol(leg.symbol)}
                            {leg.reentry > 0 && (
                              <span style={{ marginLeft: 7, padding: "2px 7px", borderRadius: 5, fontSize: 10.5, fontWeight: 700, background: "#fef3c7", color: "#92400e" }}>↻ Re-entry #{leg.reentry}</span>
                            )}
                            {(cryptoTz || stratFor(t)?._id || isOptionSymbol(leg.symbol)) && (
                              <a href={chartHref(t, leg)} target="_blank" rel="noopener noreferrer" title="Open the candle chart at this trade's date — with entry/exit (and SL/Target / inside-bar when applicable) overlaid"
                                style={{ marginLeft: 8, padding: "2px 7px", borderRadius: 5, fontSize: 10.5, fontWeight: 700, background: "#eff6ff", color: "#2563eb", textDecoration: "none", whiteSpace: "nowrap" }}>📈 View chart</a>
                            )}
                          </td>
                          <td style={C2.td}>
                            <span style={{ fontWeight: 700, color: leg.action === "SELL" ? "#dc2626" : "#16a34a" }}>
                              {leg.action === "SELL" ? "−" : "+"}{leg.qty}
                            </span>
                            <span style={{ marginLeft: 5, fontSize: 10, fontWeight: 700, color: leg.action === "SELL" ? "#dc2626" : "#16a34a" }}>{leg.action === "SELL" ? "SELL" : "BUY"}</span>
                          </td>
                          <td style={C2.td}>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>{cur}{leg.entryPrice}</div>
                            <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600 }}>{showDT(leg.entryDate || t.fullDate, leg.entryTime)}</div>
                            {leg.spotEntry != null && (
                              <div style={{ fontSize: 11, color: "#9ca3af" }}>Spot {leg.spotEntry.toLocaleString()}</div>
                            )}
                          </td>
                          <td style={C2.td}>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>{cur}{leg.exitPrice}</div>
                            <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600 }}>{showDT(leg.exitDate || t.fullDate, leg.exitTime)}</div>
                            {leg.spotExit != null && (
                              <div style={{ fontSize: 11, color: "#9ca3af" }}>Spot {leg.spotExit.toLocaleString()}</div>
                            )}
                          </td>
                          <td style={C2.td} title="Black-Scholes |Δ| at entry → exit (IV implied from the leg's own premium)">
                            <div style={{ fontSize: 12, fontWeight: 700, color: leg.entryDelta != null && leg.entryDelta >= 0.30 ? "#b91c1c" : "#0f172a" }}>
                              {leg.entryDelta != null ? "Δ " + leg.entryDelta.toFixed(2) : "—"}
                            </div>
                            <div style={{ fontSize: 11, color: leg.exitDelta != null && leg.exitDelta >= 0.30 ? "#dc2626" : "#9ca3af", fontWeight: 600 }}>
                              {leg.exitDelta != null ? "→ " + leg.exitDelta.toFixed(2) : "→ —"}
                            </div>
                          </td>
                          <td style={C2.td}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: leg.pnl >= 0 ? "#10b981" : "#ef4444" }}>
                              {leg.pnl >= 0 ? "+" : ""}{leg.pnl.toLocaleString()}
                            </span>
                          </td>
                          <td style={C2.td}>
                            <span title="Estimated brokerage + taxes for this leg (entry + exit)" style={{ fontSize: 12.5, fontWeight: 600, color: "#b45309" }}>
                              −{cur}{cur === "$" ? legCh.toFixed(2) : Math.round(legCh).toLocaleString()}
                            </span>
                          </td>
                          <td style={C2.td}>
                            <span style={{ padding: "3px 8px", borderRadius: 5, fontSize: 11, fontWeight: 700, background: leg.action === "SELL" ? "#fee2e2" : "#dcfce7", color: leg.action === "SELL" ? "#dc2626" : "#16a34a" }}>
                              {leg.action}
                            </span>
                          </td>
                          <td style={C2.td}>
                            <span style={{ padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 800, background: ex.bg, color: ex.color, whiteSpace: "nowrap" }}>{ex.label}</span>
                          </td>
                        </tr>
                        );
                      });
                      return (
                        <React.Fragment key={`cyc-${i}`}>
                          {legRows}
                          {t.deltaSeries && t.deltaSeries.length > 0 && (
                            <tr><td colSpan={8} style={{ padding: 0 }}><DeltaTrace series={t.deltaSeries} /></td></tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
                </div>
                <div style={{ padding: "8px 20px", fontSize: 12, color: "#9ca3af", background: "#fafafa" }}>
                  {(() => {
                    const shown = matchTrades.reduce((s, t) => s + (anyFilter ? (t.legs || []).filter(legMatch).length : (t.legs ? t.legs.length : 2)), 0);
                    const total = trades.reduce((s, t) => s + (t.legs ? t.legs.length : 2), 0);
                    return anyFilter ? `Showing ${shown} of ${total} executions (filtered)` : `Showing 1 - ${shown} of ${shown} executions`;
                  })()}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const C = {
  card: { background: "#fff", border: "1px solid #e8eaf0", borderRadius: 12, padding: "20px 24px", marginBottom: 16 },
  lbl: { fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" },
};

const MetricCard = ({ label, value, color, sub }) => (
  <div style={{ background: "#fff", border: "1px solid #e8eaf0", borderRadius: 10, padding: "16px 20px" }}>
    <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 800, color: color || "#1f2937" }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>{sub}</div>}
  </div>
);

// Full backtest result view: summary metrics, data-source badge, not-simulated
// caveat, an optional AI-approval gate, and the tabbed charts + trade log.
// Shared by the Backtesting page and the inline panel in Strategy Builder.
export default function BacktestResults({ results, pendingApproval, approving, onApprove, strategies }) {
  const [focusReq, setFocusReq] = useState(null); // calendar-day → Transaction Details navigation
  const [tip, setTip] = useState(null);           // cursor-following day-P&L tooltip {x,y,text,pos}
  const [feeMode, setFeeMode] = useState('taker'); // crypto: Taker (market) vs Maker (limit) fees
  if (!results) return null;

  // Estimated brokerage + taxes on the simulated trades, and the resulting net.
  // Cash-equity strategies use a different (intraday) rate table than F&O — the
  // backend tags the result with assetClass so the right rates are applied. Crypto can
  // toggle Taker (market) vs the cheaper Maker (limit-order) fee schedule.
  const isEquity = results.assetClass === 'equity';
  const isCryptoRes = String(results.assetClass || '').startsWith('crypto');
  const charges = tradeLogCharges(results.tradeLog, results.assetClass, isCryptoRes ? feeMode : undefined);
  const gross = results.totalPnl || 0;
  const net = gross - charges;

  // Derived day-level analytics for the infographic summary (computed from the
  // trade log so they stay correct without any backend change). Multiple trade
  // cycles on the same date are summed into one day before measuring streaks etc.
  const stats = (() => {
    const byDay = {};
    (results.tradeLog || []).forEach(t => { byDay[t.fullDate] = (byDay[t.fullDate] || 0) + (t.pnl || 0); });
    const dates = Object.keys(byDay).sort((a, b) => new Date(a) - new Date(b));
    const dayPnls = dates.map(d => byDay[d]);
    const winDays = dayPnls.filter(p => p > 0), lossDays = dayPnls.filter(p => p < 0);
    const grossWin = winDays.reduce((s, p) => s + p, 0);
    const grossLoss = Math.abs(lossDays.reduce((s, p) => s + p, 0));
    const avgWin = winDays.length ? grossWin / winDays.length : 0;
    const avgLoss = lossDays.length ? grossLoss / lossDays.length : 0;
    let mWin = 0, mLoss = 0, cw = 0, cl = 0;
    for (const p of dayPnls) {
      if (p > 0) { cw++; cl = 0; if (cw > mWin) mWin = cw; }
      else if (p < 0) { cl++; cw = 0; if (cl > mLoss) mLoss = cl; }
      else { cw = 0; cl = 0; }
    }
    return {
      days: dates.length, winDays: winDays.length, lossDays: lossDays.length,
      grossWin, grossLoss,
      profitFactor: grossLoss > 0 ? grossWin / grossLoss : (grossWin > 0 ? Infinity : 0),
      avgWin, avgLoss, rr: avgLoss > 0 ? avgWin / avgLoss : 0,
      bestDay: dayPnls.length ? Math.max(...dayPnls) : 0,
      worstDay: dayPnls.length ? Math.min(...dayPnls) : 0,
      avgDaily: dates.length ? dayPnls.reduce((s, p) => s + p, 0) / dates.length : 0,
      maxWinStreak: mWin, maxLossStreak: mLoss,
      dayWinRate: dates.length ? Math.round(winDays.length / dates.length * 100) : 0,
    };
  })();
  // Signed currency formatter for the stat tiles (−/+ prefix, no decimals).
  const CUR = curSymbol(results.assetClass);
  const money = (v) => `${v >= 0 ? "+" : "−"}${CUR}${Math.abs(Math.round(v)).toLocaleString()}`;
  const G = "#10b981", R = "#ef4444", BLUE = "#2563eb", INK = "#1f2937";

  return (
    <>
      {/* Summary row — headline is NET P&L (after brokerage + taxes). */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 16 }}>
        <MetricCard label="Net P&L (after charges)" value={`${net >= 0 ? "+" : ""}${CUR}${net.toLocaleString()}`} color={net >= 0 ? "#10b981" : "#ef4444"} sub={`Gross ${CUR}${gross.toLocaleString()} · Charges ${CUR}${charges.toLocaleString()}`} />
        <MetricCard label="Max Draw down" value={`${CUR}${results.maxDrawdown?.toLocaleString()}`} color="#ef4444" />
        <MetricCard label="Win Rate" value={`${results.winRate}%`} color="#2563eb" sub={`${results.wins} W / ${results.losses} L`} />
        <MetricCard label="Total Trades" value={results.totalTrades} sub={`${stats.days} trading days`} />
      </div>

      {/* Charges breakdown — gross → charges → net */}
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, padding: "10px 14px", marginBottom: 16, borderRadius: 10, background: "#f8fafc", border: "1px solid #e8eaf0", fontSize: 12.5, color: "#475569" }}>
        <span style={{ fontWeight: 700 }}>💰 Charges (est.)</span>
        <span>Gross P&L <b style={{ color: gross >= 0 ? "#10b981" : "#ef4444" }}>{CUR}{gross.toLocaleString()}</b></span>
        <span style={{ color: "#94a3b8" }}>−</span>
        <span>Brokerage + Taxes <b style={{ color: "#dc2626" }}>{CUR}{charges.toLocaleString()}</b></span>
        <span style={{ color: "#94a3b8" }}>=</span>
        <span>Net P&L <b style={{ color: net >= 0 ? "#10b981" : "#ef4444" }}>{CUR}{net.toLocaleString()}</b></span>
        {isCryptoRes && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, marginLeft: "auto" }}>
            <span style={{ fontSize: 11, color: "#94a3b8" }}>Order type:</span>
            {[["taker", "Market (Taker)"], ["maker", "Limit (Maker)"]].map(([v, l]) => (
              <button key={v} onClick={() => setFeeMode(v)} style={{
                padding: "3px 9px", fontSize: 11, fontWeight: 700, borderRadius: 6, cursor: "pointer",
                border: "1px solid " + (feeMode === v ? "#2563eb" : "#e2e8f0"),
                background: feeMode === v ? "#2563eb" : "#fff", color: feeMode === v ? "#fff" : "#64748b",
              }}>{l}</button>
            ))}
          </span>
        )}
        <span style={{ width: "100%", fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
          {String(results.assetClass || '').startsWith('crypto')
            ? `Delta Exchange fees (${feeMode === 'maker' ? 'MAKER / limit order' : 'TAKER / market order'}): Futures/Perp ${feeMode === 'maker' ? '0.02%' : '0.05%'} of notional · Options ${feeMode === 'maker' ? '0.01%' : '0.03%'} of underlying notional (capped 3.5% of premium) · +18% GST. NO brokerage/STT/stamp. P&L in USD. 💡 Use LIMIT orders to pay the cheaper Maker fee. (India 1% TDS + 30% gains tax are settlement taxes, not shown here.)`
            : isEquity
            ? "Equity intraday (MIS): 0.03%/order brokerage (cap ₹20) · 0.025% STT (sell) · 0.00297% exchange · 18% GST · 0.003% stamp — actual may vary by broker."
            : "₹20/order brokerage · 0.125% STT (sell) · 0.032% exchange · 18% GST · 0.003% stamp — calibrated to a real Zerodha statement (June 2026); actual may vary by broker."}
        </span>
      </div>

      {/* Data-source badge — which feed powered this backtest */}
      {results.dataMode && (() => {
        const dm = String(results.dataMode);
        const meta = dm.includes("Delta")
          ? { bg: "#ecfdf5", border: "#a7f3d0", color: "#065f46", dot: "🟢", title: "Real Delta Exchange data", note: "BTC/ETH 1-min candles · crypto 24/7 (UTC)" }
          : dm.includes("Upstox")
          ? { bg: "#ecfdf5", border: "#a7f3d0", color: "#065f46", dot: "🟢", title: "Real Upstox data", note: "expired option candles · history back to ~Oct 2024" }
          : dm.includes("Zerodha")
            ? { bg: "#eff6ff", border: "#bfdbfe", color: "#1e40af", dot: "🔵", title: "Real Zerodha data", note: "live contracts only · capped at ~60 days" }
            : { bg: "#fff7ed", border: "#fed7aa", color: "#9a3412", dot: "🟡", title: "Estimated (Black-Scholes)", note: "no real option candles for this range — premiums modelled" };
        return (
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, padding: "10px 14px", marginBottom: 16, borderRadius: 10, background: meta.bg, border: `1px solid ${meta.border}`, color: meta.color, fontSize: 12.5 }}>
            <span style={{ fontWeight: 800 }}>{meta.dot} Data source: {meta.title}</span>
            <span style={{ opacity: 0.85 }}>· {meta.note}</span>
            {(results.from && results.to) && <span style={{ marginLeft: "auto", opacity: 0.85 }}>{fmtDate(results.from)} → {fmtDate(results.to)}</span>}
            <span style={{ opacity: 0.7, fontSize: 11.5, width: "100%" }}>
              {dm}{results.skippedNoData > 0 ? ` · ${results.skippedNoData} day(s) skipped (no real option data — never estimated)` : ""}
            </span>
          </div>
        );
      })()}

      {/* Not-simulated caveat — advanced features the backtester ignores */}
      {results.notSimulated?.length > 0 && (
        <div style={{ padding: "10px 14px", marginBottom: 16, borderRadius: 10, background: "#fff7ed", border: "1px solid #fed7aa", color: "#9a3412", fontSize: 12.5 }}>
          <div style={{ fontWeight: 800, marginBottom: 4 }}>⚠️ P&L excludes some strategy features</div>
          <div style={{ opacity: 0.9 }}>
            The backtester does not simulate: {results.notSimulated.join(", ")}.
            These affect real results, so treat this P&L as an optimistic estimate for strategies that rely on them.
          </div>
        </div>
      )}

      {/* AI-strategy approval gate */}
      {pendingApproval && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "12px 16px", marginBottom: 16, borderRadius: 10, background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46" }}>
          <div style={{ fontSize: 12.5, flex: 1, minWidth: 200 }}>
            <b>“{pendingApproval.name}” is an AI-generated strategy.</b> Review this backtest — if it looks right, approve it to allow live deployment.
          </div>
          <button onClick={() => onApprove(false)} disabled={approving} style={{ background: "linear-gradient(135deg,#10b981,#059669)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 12.5, fontWeight: 700, cursor: approving ? "not-allowed" : "pointer", opacity: approving ? 0.65 : 1, whiteSpace: "nowrap" }}>
            {approving ? "Working…" : "✓ Approve"}
          </button>
        </div>
      )}

      {/* ── Backtest Summary ── */}
      <div>
          <div style={{ ...C.card, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, color: "#1f2937" }}>Backtest Summary</div>

            {/* Headline P&L banner */}
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16, padding: "16px 20px", marginBottom: 18, borderRadius: 14, background: net >= 0 ? "linear-gradient(120deg,#ecfdf5,#d1fae5)" : "linear-gradient(120deg,#fef2f2,#fee2e2)", border: `1px solid ${net >= 0 ? "#a7f3d0" : "#fecaca"}` }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>Net P&amp;L (after charges)</div>
                <div style={{ fontSize: 30, fontWeight: 900, color: net >= 0 ? "#047857" : "#b91c1c", lineHeight: 1.1, marginTop: 2 }}>{money(net)}</div>
                <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 3 }}>Gross {CUR}{gross.toLocaleString()} · Charges {CUR}{charges.toLocaleString()}</div>
              </div>
              <div style={{ marginLeft: "auto", textAlign: "right" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>Win Rate</div>
                <div style={{ fontSize: 26, fontWeight: 900, color: BLUE }}>{results.winRate}%</div>
                <div style={{ fontSize: 11.5, color: "#64748b" }}>{results.wins}W / {results.losses}L trades</div>
              </div>
            </div>

            {/* Win / loss DAY proportion bar */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, fontWeight: 700, marginBottom: 6 }}>
                <span style={{ color: G }}>▲ {stats.winDays} win days</span>
                <span style={{ color: "#64748b" }}>{stats.dayWinRate}% of {stats.days} days green</span>
                <span style={{ color: R }}>{stats.lossDays} loss days ▼</span>
              </div>
              <div style={{ display: "flex", height: 12, borderRadius: 99, overflow: "hidden", background: "#eef2f7" }}>
                <div style={{ width: `${stats.dayWinRate}%`, background: "linear-gradient(90deg,#34d399,#10b981)" }} />
                <div style={{ flex: 1, background: "linear-gradient(90deg,#f87171,#ef4444)" }} />
              </div>
            </div>

            {/* Infographic stat tiles */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(148px, 1fr))", gap: 12 }}>
              {[
                ["⚖️", "Profit Factor", stats.profitFactor === Infinity ? "∞" : stats.profitFactor.toFixed(2), stats.profitFactor >= 1 ? G : R, "gross win ÷ gross loss"],
                ["📈", "Avg / Day", money(stats.avgDaily), stats.avgDaily >= 0 ? G : R, `over ${stats.days} days`],
                ["↕️", "Risk : Reward", stats.rr ? `1 : ${stats.rr.toFixed(2)}` : "—", BLUE, "avg win ÷ avg loss day"],
                ["📊", "Sharpe Ratio", results.sharpe, BLUE, "risk-adjusted return"],
                ["🟢", "Avg Win Day", money(stats.avgWin), G, `${stats.winDays} days`],
                ["🔴", "Avg Loss Day", money(-stats.avgLoss), R, `${stats.lossDays} days`],
                ["🏆", "Best Day", money(stats.bestDay), G, "biggest green day"],
                ["💥", "Worst Day", money(stats.worstDay), R, "biggest red day"],
                ["🔥", "Max Win Streak", `${stats.maxWinStreak} days`, G, "consecutive green"],
                ["🧊", "Max Loss Streak", `${stats.maxLossStreak} days`, R, "consecutive red"],
                ["📉", "Max Drawdown", money(results.maxDrawdown || 0), R, "peak-to-trough"],
                ["📆", "Trading Days", stats.days, INK, `${results.totalTrades} total trades`],
              ].map(([icon, label, value, color, sub]) => (
                <div key={label} style={{ background: "#f8fafc", border: "1px solid #eef1f6", borderRadius: 12, padding: "12px 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 6 }}>
                    <span style={{ fontSize: 15 }}>{icon}</span>
                    <span style={{ fontSize: 10.5, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</span>
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 800, color }}>{value}</div>
                  {sub && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>{sub}</div>}
                </div>
              ))}
            </div>
          </div>
          {/* ── Daywise Breakdown — a real month calendar (each P&L in its true
                weekday column; weekends / non-trading days stay blank) ── */}
          {results.calendar && (
            <div style={{ ...C.card, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, color: "#1f2937" }}>Daywise Breakdown</div>
              {/* Scale color intensity by P&L magnitude: bigger profit → deeper green,
                 bigger loss → deeper red. maxAbs is the largest single-day |P&L| across
                 every month, so shades are comparable on one global scale. */}
              {(() => {
                let maxAbs = 0;
                Object.values(results.calendar).forEach(days => {
                  const byDay = {};
                  days.forEach(d => { byDay[d.day] = (byDay[d.day] || 0) + d.pnl; });
                  Object.values(byDay).forEach(v => { maxAbs = Math.max(maxAbs, Math.abs(v)); });
                });
                // Interpolate a fill (light → deep) and pick a readable text color from
                // the day's P&L relative to the global max.
                const lerp = (a, b, t) => Math.round(a + (b - a) * t);
                const shade = pnl => {
                  const ratio = maxAbs > 0 ? Math.min(1, Math.abs(pnl) / maxAbs) : 0;
                  const t = 0.18 + 0.82 * ratio; // lighter floor so small P&L stays soft/white-leaning
                  const [lo, hi] = pnl >= 0
                    ? [[236, 253, 245], [5, 150, 105]]   // green: #ecfdf5 → #059669
                    : [[254, 242, 242], [220, 38, 38]];  // red:   #fef2f2 → #dc2626
                  const bg = `rgb(${lerp(lo[0], hi[0], t)},${lerp(lo[1], hi[1], t)},${lerp(lo[2], hi[2], t)})`;
                  return { bg, fg: t > 0.5 ? "#fff" : (pnl >= 0 ? "#065f46" : "#991b1b") };
                };
              return (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
                {Object.entries(results.calendar).map(([month, days]) => {
                  const monthPnl = days.reduce((s, d) => s + d.pnl, 0);
                  // P&L + real date per day-of-month, and the month grid derived from a
                  // real date so every cell sits under its actual weekday (no left-to-
                  // right fill). dateByDay maps a cell to the exact key used to group the
                  // transactions, so a click can scroll straight to that day.
                  const pnlByDay = {}, dateByDay = {};
                  days.forEach(d => { pnlByDay[d.day] = (pnlByDay[d.day] || 0) + d.pnl; dateByDay[d.day] = d.date; });
                  const first = new Date((days[0] && days[0].date ? days[0].date : "") + "T00:00:00");
                  const year = first.getFullYear(), mon = first.getMonth();
                  const firstDow = new Date(year, mon, 1).getDay();          // 0 = Sunday
                  const daysInMonth = new Date(year, mon + 1, 0).getDate();
                  const cells = [];
                  for (let i = 0; i < firstDow; i++) cells.push(null);       // leading blanks
                  for (let dnum = 1; dnum <= daysInMonth; dnum++) cells.push(dnum);
                  while (cells.length < 42) cells.push(null);                 // pad to 6 rows → every month same height
                  return (
                    <div key={month} style={{ background: "#fff", border: "1px solid #eef1f6", borderRadius: 9, padding: 7, boxShadow: "0 1px 3px rgba(15,23,42,0.04)" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                        <span style={{ fontSize: 10.5, fontWeight: 700, color: "#1f2937" }}>{month}</span>
                        <span style={{ fontSize: 9.5, fontWeight: 700, color: monthPnl >= 0 ? "#059669" : "#dc2626" }}>
                          {monthPnl >= 0 ? "+" : ""}{CUR}{monthPnl.toLocaleString()}
                        </span>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
                        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                          <div key={"h" + i} style={{ fontSize: 8, fontWeight: 700, textAlign: "center", paddingBottom: 1, color: (i === 0 || i === 6) ? "#d1d5db" : "#9ca3af" }}>{d}</div>
                        ))}
                        {cells.map((dnum, i) => {
                          if (dnum === null) return <div key={"b" + i} style={{ height: 18 }} />;
                          const has = Object.prototype.hasOwnProperty.call(pnlByDay, dnum);
                          const pnl = pnlByDay[dnum] || 0;
                          const sh = has ? shade(pnl) : null;
                          const bg = has ? sh.bg : "#fff";
                          const label = `${dnum} ${month}: ${pnl >= 0 ? "+" : ""}${CUR}${pnl.toLocaleString()}`;
                          return (
                            <div key={i}
                              onMouseMove={has ? (e) => setTip({ x: e.clientX, y: e.clientY, text: label, pos: pnl >= 0 }) : undefined}
                              onMouseLeave={has ? () => setTip(null) : undefined}
                              onClick={has ? () => { setTip(null); setFocusReq(p => ({ date: dateByDay[dnum], n: (p ? p.n : 0) + 1 })); } : undefined}
                              style={{ height: 18, borderRadius: 4, background: bg, border: has ? "none" : "1px solid #f4f6fa", display: "flex", alignItems: "center", justifyContent: "center", cursor: has ? "pointer" : "default" }}>
                              <span style={{ fontSize: 8.5, fontWeight: 700, color: has ? sh.fg : "#cbd5e1" }}>{dnum}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              ); })()}
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 12 }}>Tip: hover a day for its P&amp;L · click to jump to that day’s transactions.</div>
            </div>
          )}
          {/* Area chart */}
          <div style={C.card}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, color: "#1f2937" }}>P&L Over Time</div>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={results.chart}>
                <defs>
                  <linearGradient id="pnlG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f3f9" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={Math.floor((results.chart?.length || 8) / 8)} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${CUR}${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={v => [`${CUR}${v.toLocaleString()}`, "P&L"]} />
                <Area type="monotone" dataKey="pnl" stroke="#2563eb" strokeWidth={2} fill="url(#pnlG)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

      {/* ── Transaction Details (last section, below the summary) ── */}
      {results.tradeLog && (
        <div style={{ marginBottom: 16 }}>
          <TradeHistory tradeLog={results.tradeLog} focusReq={focusReq} strategies={strategies} feeMode={isCryptoRes ? feeMode : undefined} />
        </div>
      )}

      {/* Cursor-following tooltip showing the hovered day's P&L */}
      {tip && (
        <div style={{ position: "fixed", left: tip.x, top: tip.y - 42, transform: "translateX(-50%)", zIndex: 1000, pointerEvents: "none", background: "#0f172a", color: "#fff", padding: "5px 9px", borderRadius: 6, fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap", boxShadow: "0 6px 16px rgba(15,23,42,0.28)" }}>
          {tip.text}
        </div>
      )}
    </>
  );
}
